import { Component, Fragment } from 'react';
import type { ErrorInfo, PropsWithChildren, ReactNode } from 'react';

import { SystemBanner } from '../foundation/state';

export interface RouteRecoveryAction {
  readonly scopeKey: string;
  readonly recover: () => void;
}

interface RouteRecoveryBoundaryProps extends PropsWithChildren {
  readonly scopeKey: string;
  readonly onRecoveryChange: (action: RouteRecoveryAction | null) => void;
  readonly onPresentationFailed: () => void;
}

interface RouteRecoveryBoundaryState {
  readonly failed: boolean;
  readonly generation: number;
}

/** Catches only disposable presentation. Its owner, ports and pending work are
 * deliberately above this boundary and survive a named recovery attempt. */
export class RouteRecoveryBoundary extends Component<RouteRecoveryBoundaryProps, RouteRecoveryBoundaryState> {
  state: RouteRecoveryBoundaryState = { failed: false, generation: 0 };

  private readonly recover = () => {
    this.props.onRecoveryChange(null);
    this.setState((state) => ({ failed: false, generation: state.generation + 1 }));
  };

  static getDerivedStateFromError(): Partial<RouteRecoveryBoundaryState> {
    return { failed: true };
  }

  componentDidCatch(_error: Error, _info: ErrorInfo) {
    this.props.onPresentationFailed();
    this.props.onRecoveryChange({ scopeKey: this.props.scopeKey, recover: this.recover });
  }

  componentWillUnmount() {
    this.props.onRecoveryChange(null);
  }

  render(): ReactNode {
    if (this.state.failed) return <section data-route-presentation-error="tpv">
      <SystemBanner tone="critical" urgent>La vista del TPV se ha interrumpido. Tu sesión y el trabajo pendiente siguen conservados.</SystemBanner>
    </section>;
    return <Fragment key={this.state.generation}>{this.props.children}</Fragment>;
  }
}

export function RouteRecoveryStatus({ action }: { readonly action: RouteRecoveryAction | null }) {
  if (action === null) return null;
  return <div className="runtime-failure" data-runtime-error="route-presentation">
    <SystemBanner tone="critical" urgent>La vista del TPV necesita recuperarse. No se repetirá ninguna operación automáticamente.</SystemBanner>
    <button className="btn btn--secondary" onClick={action.recover} type="button">Recuperar vista del TPV</button>
  </div>;
}
